import { translate } from '@util/Localization';
import { trialPeriodEndDate } from '@util/TrialDateUtils';
import { useUnbounce } from '@util/Unbounce';
import { App, TrialPlan } from 'hydra-data-types';
import React, { ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { isDefined, isNonEmptyString } from 'ts-utils';
import {
  getUtmCampaignSearchParam,
  getUtmContentSearchParam,
  getUtmMediumSearchParam,
  getUtmSourceSearchParam,
  getUtmTermSearchParam,
} from '../../config/AssessmentUtils';
import { Color } from '../../config/Color';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { AppSelector, store, useAppStore } from '../../store/Store';
import { Analytics } from '../../util/Analytics';
import { appOverride } from '../../util/AppUtils';
import { isDirectMedicareFlow } from '../../util/MedicareAccess';
import { DownloadAppButtons } from '../DownloadAppButtons';
import { ScreenWrapper } from '../styles/Containers';
import { BodyXXS, Heading3 } from '../styles/Typography';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout } from './layouts/PresentationLayout';

/**
 * Dynamic detail appended to a step, resolved from state rather than config copy: the email the user
 * signed up with, or when their trial ends.
 */
type StepDetail = 'email' | 'trialExpiry';

// Because we're not exposing the PresentationLayoutConfig directly we have a separate layout config
type DownloadAppLayoutConfig = {
  title: string[];
  body?: string[];
  alreadyPaid: boolean;
  /** When provided, renders the rich "You're all set" layout (numbered steps) instead of the default. */
  steps?: Array<{ title: string; description?: string; detail?: StepDetail }>;
  /** Optional help card rendered below the steps layout. */
  help?: {
    title: string;
    subtitle?: string;
    items: string[];
  };
};

export type DownloadAppScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.DownloadApp,
  DownloadAppLayoutConfig,
  ScreenNames
>;

type DownloadAppScreenProps<ScreenNames extends string> = NavigationProps<
  DownloadAppScreenConfig<ScreenNames>,
  ScreenNames
>;

export const DownloadAppScreen = <ScreenNames extends string>(
  props: DownloadAppScreenProps<ScreenNames>
): ReactElement => {
  const { layout } = props;

  useEffect(function trackDownloadAndFlowCompletion() {
    Analytics.track({ event: Analytics.Event.o_download_app_screen_viewed });
    Analytics.track({
      event: Analytics.Event.o_flow_completed,
      utm_source: getUtmSourceSearchParam() ?? null,
      utm_medium: getUtmMediumSearchParam() ?? null,
      utm_campaign: getUtmCampaignSearchParam() ?? null,
      utm_term: getUtmTermSearchParam() ?? null,
      utm_content: getUtmContentSearchParam() ?? null,
      coupon: store.getState().pricing.couponCode,
    });
  }, []);

  const { user, trialEndDate, addonProduct, addonPurchased, trialPlan } = useAppStore((state) => ({
    user: AppSelector.user(state),
    trialEndDate: trialPeriodEndDate(state.purchase.trialPeriodDays, 'Do MMMM, YYYY'),
    addonProduct: state.purchase.addonProduct,
    addonPurchased: state.purchase.addonPurchased,
    trialPlan: state.purchase.trialPlan,
  }));

  const isDirectMedicare = useAppStore(isDirectMedicareFlow);

  const hasTrial = trialPlan !== TrialPlan.None;

  const showTrialExpiry = hasTrial && !layout.alreadyPaid && !isDirectMedicare;

  const trialExpiryInfo = showTrialExpiry
    ? [
        {
          type: 'custom' as const,
          content: (
            <BodyText style={{ paddingTop: 35 }}>
              Your trial will expire on the {trialEndDate}.
            </BodyText>
          ),
        },
      ]
    : [];

  function renderStepDetail(detail: StepDetail | undefined): ReactElement | undefined {
    switch (detail) {
      case 'email':
        return isNonEmptyString(user?.email) ? <StepEmail>{user?.email}</StepEmail> : undefined;
      case 'trialExpiry':
        return showTrialExpiry ? (
          <StepDescription>Your trial expires on the {trialEndDate}.</StepDescription>
        ) : undefined;
      default:
        return undefined;
    }
  }

  const showMoodAccessCopy = addonProduct === App.Mood && addonPurchased;

  useUnbounce();

  // The steps layout centres a larger title with the body tucked under it as a subtitle, so it
  // renders the body here rather than as separate paragraphs further down.
  const stepsTitleContent = (
    <div style={{ margin: 'auto', paddingTop: 15, textAlign: 'center' }}>
      {layout.title.map((content, index) => (
        <StepsTitle key={index}>{content}</StepsTitle>
      ))}
      {layout.body?.map((text, index) => (
        <StepsSubtitle key={index}>{text}</StepsSubtitle>
      ))}
    </div>
  );

  const titleContent = (
    <div style={{ margin: 'auto', paddingTop: 15, textAlign: 'center' }}>
      {layout.title.map((content, index) => (
        <CustomHeader key={index}>{content} </CustomHeader>
      ))}
      {/* The steps layout spells out signing in as its own numbered step, so it doesn't need this. */}
      {layout.alreadyPaid && !isDefined(layout.steps) && (
        <CustomHeader>Login to the {translate('product')} app</CustomHeader>
      )}
    </div>
  );

  if (isDefined(layout.steps)) {
    return (
      <MainContainer data-test="DownloadAppScreen">
        <PresentationLayout
          content={[
            { type: 'custom', content: stepsTitleContent },
            { type: 'custom', content: <StepsHeading>What happens next</StepsHeading> },
            {
              type: 'custom',
              content: (
                <StepsList>
                  {layout.steps.map((step, index) => (
                    <StepRow key={index}>
                      <StepNumber>{index + 1}</StepNumber>
                      <div>
                        <StepTitle>{step.title}</StepTitle>
                        {isDefined(step.description) && (
                          <StepDescription>{step.description}</StepDescription>
                        )}
                        {renderStepDetail(step.detail)}
                      </div>
                    </StepRow>
                  ))}
                </StepsList>
              ),
            },
            {
              type: 'custom',
              content: (
                <StepsButtonsContainer>
                  <DownloadAppButtons />
                </StepsButtonsContainer>
              ),
            },
            ...(isDefined(layout.help)
              ? [
                  {
                    type: 'custom' as const,
                    content: (
                      <HelpCard>
                        <HelpTitle>{layout.help.title}</HelpTitle>
                        {isDefined(layout.help.subtitle) && (
                          <BodyText>{layout.help.subtitle}</BodyText>
                        )}
                        {layout.help.items.map((item, index) => (
                          <HelpItem key={index}>{item}</HelpItem>
                        ))}
                      </HelpCard>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </MainContainer>
    );
  }

  return (
    <MainContainer data-test="DownloadAppScreen">
      <PresentationLayout
        content={[
          { type: 'custom', content: titleContent },
          {
            type: 'custom',
            content: (
              <BodyContainer>
                {layout.alreadyPaid ? (
                  layout.body?.map((content, index) => <BodyText key={index}>{content}</BodyText>)
                ) : (
                  <>
                    <BodyText>
                      {' '}
                      Login to the {translate('product')} app using the same email and password you
                      entered before.
                    </BodyText>
                    {showMoodAccessCopy && (
                      <BodyText>
                        The Mental Health program details will be sent to your email.
                      </BodyText>
                    )}
                  </>
                )}
              </BodyContainer>
            ),
          },
          // A returning user (e.g. reopening the referral link) has no email in the store, so
          // skip the pill rather than render it empty
          ...(isNonEmptyString(user?.email)
            ? [
                {
                  type: 'custom' as const,
                  content: <EmailContainer data-test="UserEmail">{user?.email}</EmailContainer>,
                },
              ]
            : []),
          ...trialExpiryInfo,
          {
            type: 'custom' as const,
            content: (
              // wrapped the button and bottom text together in a container so that we can
              // easily position it to the bottom, and have fixed spacing between the button and text
              <FixedBottomContainer>
                <ButtonsContainer>
                  <DownloadAppButtons />
                </ButtonsContainer>
                <ContactUsTextContainer>
                  <BodyText>
                    Contact us anytime through the in-app chat, or via email at{' '}
                    <a href={`mailto:${translate('links.supportEmail')}`}>
                      {translate('links.supportEmail')}
                    </a>
                    .
                  </BodyText>
                </ContactUsTextContainer>
              </FixedBottomContainer>
            ),
          },
        ]}
      />
    </MainContainer>
  );
};

const BodyContainer = styled(ScreenWrapper)`
  margin: auto;
`;

const BodyText = styled(BodyXXS)`
  text-align: center;
`;

const CustomHeader = styled(Heading3)`
  margin-bottom: 0;
`;

const StepsTitle = styled(Heading3)`
  font-size: 28px;
  margin: 0;
  text-align: center;
`;

const StepsSubtitle = styled(BodyXXS)`
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin: ${({ theme }) => `${theme.spacing._4px} 0 0`};
  text-align: center;
`;

const StepsHeading = styled(Heading3)`
  margin: 24px 0 12px;
  text-align: center;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  margin: 0 auto;
`;

const StepRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
`;

const StepNumber = styled.div`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

// Medium weight at body size, so a step title reads as a label rather than a heading.
const StepTitle = styled.div`
  color: white;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const StepDescription = styled.div`
  color: white;
  opacity: 0.85;
  font-size: 14px;
  line-height: 1.4;
`;

const HelpCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 24px auto 0;
  padding: ${({ theme }) => `${theme.spacing._24px} ${theme.spacing._20px}`};
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  text-align: center;
`;

const HelpTitle = styled(Heading3)`
  margin: 0;
`;

const HelpItem = styled(BodyText)`
  margin: 0;
`;

const ContactUsTextContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing._16px} ${theme.spacing._46px}`};
  margin: auto;
`;

const FixedBottomContainer = styled.div`
  @media ${({ theme }) => theme.device.mobile} {
    position: absolute;
    bottom: 0%;
    left: 0;
  }
  @media not all and (${({ theme }) => theme.device.mobile}) {
    margin-top: ${({ theme }) => theme.spacing._36px};
  }
  margin: auto;
  width: 100%;
`;

const MainContainer = styled(ScreenWrapper)`
  background: ${appOverride({ nerva: Color.gradient, default: Color.gradient })};
  position: absolute;
  top: 0%;
  margin-left: -50vw;
  left: 50%;
  width: 100vw;
  height: 100vh;
  overflow-y: auto;
  padding-top: ${({ theme }) => theme.spacing._66px};

  /* This screen spans the viewport for its background; keep the content in the usual column. */
  > * {
    max-width: 600px;
    margin: 0 auto;
    padding: ${({ theme }) => `0 ${theme.spacing._20px}`};
  }
`;

const EmailContainer = styled.div`
  background-color: white;
  border-radius: 60px;
  width: fit-content;
  max-width: 310px;
  overflow-wrap: break-word;
  padding: ${({ theme }) => `${theme.spacing._16px} ${theme.spacing._26px}`};
  margin: auto;
  text-align: center;
  color: ${appOverride({
    nerva: Color.content.accent,
    default: Color.content.default,
  })};
`;

// Sits inside a numbered step rather than on its own, so it centres within the step's text column
// and uses tighter padding than the standalone pill.
const StepEmail = styled(EmailContainer)`
  margin: ${({ theme }) => `${theme.spacing._8px} auto 0`};
  padding: ${({ theme }) => `${theme.spacing._8px} ${theme.spacing._16px}`};
  font-size: ${({ theme }) => theme.fontSize.xxs};
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  & a:first-child {
    margin-right: ${({ theme }) => theme.spacing._6px};
  }

  & a:last-child {
    margin-left: ${({ theme }) => theme.spacing._6px};
  }
`;

// In the "steps" layout the buttons follow the numbered steps directly; add top spacing so the CTA
// isn't cramped against the last step.
const StepsButtonsContainer = styled(ButtonsContainer)`
  margin-top: ${({ theme }) => theme.spacing._24px};
`;
